import { useEffect, useState } from "react";
import type { MonitoringTokenStatus } from "@shared/modules/x-appels";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Icon } from "../../../core/components/ui/icon";
import { formatDateTime } from "../../../core/lib/format";
import { useToastStore } from "../../../core/stores/toast";
import { getMonitoringToken, issueMonitoringToken, revokeMonitoringToken } from "../lib/api";

/**
 * Jeton de supervision : l'outil d'Umbeli (MC) lit l'historique des appels de
 * cette instance avec ce jeton. Il est affiché une seule fois à sa création ;
 * ensuite seuls ses quatre derniers caractères restent visibles.
 */
export function SupervisionTokenCard() {
  const push = useToastStore((s) => s.push);
  const [status, setStatus] = useState<MonitoringTokenStatus | null>(null);
  const [fresh, setFresh] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getMonitoringToken()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  const issue = async () => {
    if (busy) return;
    if (
      status?.configured &&
      !window.confirm(
        "Générer un nouveau jeton remplace l'ancien : l'outil de supervision devra être mis à jour. Continuer ?",
      )
    )
      return;
    setBusy(true);
    try {
      const { token, ...rest } = await issueMonitoringToken();
      setStatus(rest);
      setFresh(token);
    } catch {
      push("danger", "Génération impossible");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (busy) return;
    if (!window.confirm("Révoquer le jeton ? La supervision ne pourra plus lire les appels.")) return;
    setBusy(true);
    try {
      await revokeMonitoringToken();
      setStatus({ configured: false, hint: null, createdAt: null });
      setFresh(null);
      push("success", "Jeton révoqué");
    } catch {
      push("danger", "Révocation impossible");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!fresh) return;
    try {
      await navigator.clipboard.writeText(fresh);
      push("success", "Jeton copié");
    } catch {
      push("warning", "Copie impossible", "Sélectionnez le jeton et copiez-le à la main.");
    }
  };

  return (
    <Card
      title="Supervision Umbeli"
      subtitle="L'outil de supervision lit l'historique des appels de cette instance, en lecture seule, avec ce jeton."
    >
      <div className="repondeur__stack">
        <dl className="kv">
          <dt>Jeton</dt>
          <dd>
            {status === null ? (
              "—"
            ) : status.configured ? (
              <Badge variant="success">Actif · …{status.hint ?? "????"}</Badge>
            ) : (
              <Badge variant="neutral">Aucun</Badge>
            )}
          </dd>
          <dt>Créé le</dt>
          <dd>{status?.createdAt ? formatDateTime(status.createdAt) : "—"}</dd>
        </dl>

        {fresh ? (
          <div className="supervision-token" role="status">
            <p className="supervision-token__warn">
              Copiez ce jeton maintenant : il ne sera plus affiché.
            </p>
            <div className="supervision-token__row">
              <input
                className="input supervision-token__value"
                readOnly
                value={fresh}
                aria-label="Jeton de supervision"
                onFocus={(e) => e.currentTarget.select()}
              />
              <Button variant="primary" icon={<Icon name="check" size={16} />} onClick={() => void copy()}>
                Copier
              </Button>
            </div>
          </div>
        ) : null}

        <div className="repondeur__test">
          <Button variant="ghost" disabled={busy} onClick={() => void issue()}>
            {status?.configured ? "Régénérer le jeton" : "Générer un jeton"}
          </Button>
          {status?.configured ? (
            <Button variant="ghost-danger" disabled={busy} onClick={() => void revoke()}>
              Révoquer
            </Button>
          ) : null}
        </div>
        <p className="repondeur__note">
          Le jeton se colle dans MC (Supervision → Sources) avec l'adresse de ce portail. Il ne
          donne accès ni aux réglages, ni aux clients, ni aux rendez-vous.
        </p>
      </div>
    </Card>
  );
}
